/**
 * BrightPath - ExpenseDetailPage
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Receipt,
  Calendar,
  User,
  Building,
  Tag,
  FileText,
  Copy,
  DollarSign,
  Shield,
} from 'lucide-react';
import { useExpense, useDeleteExpense } from '../hooks';
import { EXPENSE_CATEGORIES, CLAIM_STATUS_LABELS, RECURRENCE_PATTERNS } from '../types';
import { formatCurrency, formatDate, cn } from '@/core/lib/utils';

export function ExpenseDetailPage() {
  const { expenseId } = useParams<{ expenseId: string }>();
  const navigate = useNavigate();
  const { data: expense, isLoading } = useExpense(expenseId!);
  const { mutate: deleteExpense } = useDeleteExpense();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-pulse text-warm-gray">Loading...</div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <p className="text-warm-gray mb-4">Expense not found</p>
          <Link to="/family/expenses" className="text-sage hover:underline">
            Back to Expenses
          </Link>
        </div>
      </div>
    );
  }

  const categoryConfig = EXPENSE_CATEGORIES[expense.category];
  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this expense?')) {
      deleteExpense({ expenseId: expense.id }, {
        onSuccess: () => navigate('/family/expenses'),
      });
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-white border-b border-warm-gray/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-warm-gray hover:text-navy transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <div className="flex items-center gap-2">
              <Link
                to={`/family/expenses/${expense.id}/edit`}
                className="flex items-center gap-2 px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Link>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Main Card */}
        <div className="bg-white rounded-2xl border border-warm-gray/10 overflow-hidden">
          {/* Header Section */}
          <div className="p-6 border-b border-warm-gray/10">
            <div className="flex items-start gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${categoryConfig.color}15`, color: categoryConfig.color }}
              >
                <Receipt className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-display font-semibold text-navy mb-1">
                  {expense.description}
                </h1>
                <div className="flex items-center gap-3 text-warm-gray">
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{ backgroundColor: `${categoryConfig.color}15`, color: categoryConfig.color }}
                  >
                    {categoryConfig.label}
                  </span>
                  <span>{formatDate(expense.expenseDate)}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-navy">{formatCurrency(expense.amount)}</p>
                {expense.outOfPocket !== expense.amount && (
                  <p className="text-sm text-terracotta">
                    {formatCurrency(expense.outOfPocket)} out of pocket
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-navy flex items-center gap-2">
                <FileText className="w-4 h-4 text-warm-gray" />
                Details
              </h3>

              {expense.vendorName && (
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-warm-gray mt-0.5" />
                  <div>
                    <p className="text-sm text-warm-gray">Vendor</p>
                    <p className="text-navy">{expense.vendorName}</p>
                  </div>
                </div>
              )}

              {expense.paymentMethod && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-warm-gray mt-0.5" />
                  <div>
                    <p className="text-sm text-warm-gray">Payment Method</p>
                    <p className="text-navy capitalize">{expense.paymentMethod}</p>
                  </div>
                </div>
              )}

              {expense.childId && (
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-warm-gray mt-0.5" />
                  <div>
                    <p className="text-sm text-warm-gray">Child</p>
                    <p className="text-navy">{expense.childName || 'Unknown'}</p>
                  </div>
                </div>
              )}

              {expense.isRecurring && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-warm-gray mt-0.5" />
                  <div>
                    <p className="text-sm text-warm-gray">Recurring</p>
                    <p className="text-navy">
                      {RECURRENCE_PATTERNS[expense.recurrencePattern!]?.label}
                      {expense.recurrenceEndDate && ` until ${formatDate(expense.recurrenceEndDate)}`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Insurance Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-navy flex items-center gap-2">
                <Shield className="w-4 h-4 text-warm-gray" />
                Insurance
              </h3>

              {expense.isInsuranceCovered ? (
                <>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-blue-600">Insurance Covered</span>
                      <span className="font-semibold text-blue-700">
                        {formatCurrency(expense.insuranceAmount || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-600">Your Cost</span>
                      <span className="font-semibold text-blue-700">
                        {formatCurrency(expense.outOfPocket)}
                      </span>
                    </div>
                  </div>

                  {expense.claimNumber && (
                    <div className="flex items-start gap-3">
                      <Copy className="w-5 h-5 text-warm-gray mt-0.5" />
                      <div>
                        <p className="text-sm text-warm-gray">Claim Number</p>
                        <p className="text-navy font-mono">{expense.claimNumber}</p>
                      </div>
                    </div>
                  )}

                  {expense.claimStatus && (
                    <div>
                      <p className="text-sm text-warm-gray mb-1">Claim Status</p>
                      <span
                        className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor: `${CLAIM_STATUS_LABELS[expense.claimStatus]?.color}20`,
                          color: CLAIM_STATUS_LABELS[expense.claimStatus]?.color,
                        }}
                      >
                        {CLAIM_STATUS_LABELS[expense.claimStatus]?.label}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-warm-gray">Not covered by insurance</p>
              )}
            </div>
          </div>

          {/* Tax Section */}
          {expense.isTaxDeductible && (
            <div className="px-6 pb-6">
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Receipt className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-700">Tax Deductible</span>
                </div>
                {expense.taxCategory && (
                  <p className="text-sm text-amber-600">Category: {expense.taxCategory}</p>
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          {expense.tags && expense.tags.length > 0 && (
            <div className="px-6 pb-6">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-warm-gray" />
                {expense.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-cream rounded-full text-sm text-navy"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {expense.notes && (
            <div className="px-6 pb-6">
              <h3 className="font-semibold text-navy mb-2">Notes</h3>
              <p className="text-warm-gray whitespace-pre-wrap">{expense.notes}</p>
            </div>
          )}

          {/* Receipt */}
          {expense.receiptDocumentId && (
            <div className="p-6 border-t border-warm-gray/10">
              <Link
                to={`/family/documents/${expense.receiptDocumentId}`}
                className="flex items-center gap-3 p-4 bg-cream rounded-lg hover:bg-cream-dark transition-colors"
              >
                <FileText className="w-6 h-6 text-sage" />
                <div>
                  <p className="font-medium text-navy">View Receipt</p>
                  <p className="text-sm text-warm-gray">Attached document</p>
                </div>
              </Link>
            </div>
          )}

          {/* Metadata */}
          <div className="px-6 py-4 bg-cream/50 text-sm text-warm-gray">
            <p>Created {formatDate(expense.createdAt)}</p>
            {expense.updatedAt !== expense.createdAt && (
              <p>Last updated {formatDate(expense.updatedAt)}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExpenseDetailPage;
